(() => {
  "use strict";

  /*
   * Shopify publishes the standard commerce events listed below. They stay the
   * single source of truth for those actions; this theme never republishes them.
   * See evidence/qa/v13-2026-07-28/analytics-consent-audit.md.
   */
  const STANDARD_EVENT_COVERAGE = Object.freeze({
    product_view: "product_viewed",
    search: "search_submitted",
    collection_view: "collection_viewed",
    add_to_cart: "product_added_to_cart",
    remove_from_cart: "product_removed_from_cart",
    checkout_start: "checkout_started",
  });

  const EVENT_FIELDS = Object.freeze({
    "syvox:collection_filter_changed": [
      "collection_handle",
      "selected_filter",
      "visible_product_count",
    ],
    "syvox:collection_sort_changed": [
      "collection_handle",
      "sort_by",
    ],
    "syvox:cart_drawer_opened": [
      "open_source",
      "cart_item_count",
    ],
    "syvox:cart_drawer_closed": [
      "cart_item_count",
    ],
    "syvox:cross_sell_impression": [
      "placement",
      "product_handle",
      "source_product_handle",
      "target_product_handle",
      "variant_id",
      "recommendation_id",
    ],
    "syvox:cross_sell_clicked": [
      "placement",
      "product_handle",
      "source_product_handle",
      "target_product_handle",
      "variant_id",
      "recommendation_id",
      "action",
    ],
    "syvox:cross_sell_added": [
      "placement",
      "product_handle",
      "source_product_handle",
      "target_product_handle",
      "variant_id",
      "recommendation_id",
    ],
    "syvox:checkout_cta_clicked": [
      "placement",
      "cart_item_count",
    ],
    "syvox:search_result_clicked": [
      "product_handle",
      "result_position",
    ],
  });

  const COMMON_FIELDS = Object.freeze([
    "page_type",
    "locale",
    "source_product_handle",
  ]);
  const MAX_TEXT_LENGTH = 100;

  class SyvoxAnalyticsTracker {
    constructor({ root = document, win = window } = {}) {
      this.root = root;
      this.win = win;
      this.context = this.readContext();
      this.seenImpressions = new Set();
      this.lastDrawerState = "closed";
      this.pendingDrawerSource = "";
      this.recommendationObserver = null;
      this.drawerObserver = null;
      this._initialized = false;
    }

    readContext() {
      const node = this.root.querySelector?.("[data-sx-analytics-context]");
      if (!node) return {};

      try {
        const parsed = JSON.parse(node.textContent || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }

    analyticsAllowed() {
      try {
        return this.win.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
      } catch {
        return false;
      }
    }

    loadPrivacyApi() {
      const shopify = this.win.Shopify;
      if (
        shopify?.customerPrivacy?.analyticsProcessingAllowed ||
        typeof shopify?.loadFeatures !== "function"
      ) {
        return;
      }

      try {
        shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          (error) => {
            if (error) return;
            this.win.requestAnimationFrame(() => this.scanVisibleRecommendations());
          },
        );
      } catch {
        // Analytics stays fail-closed until Shopify exposes the privacy API.
      }
    }

    cleanString(value) {
      if (typeof value !== "string" && typeof value !== "number") return "";
      return String(value)
        .replace(/[\u0000-\u001f\u007f]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, MAX_TEXT_LENGTH);
    }

    cleanCount(value) {
      const count = Number(value);
      if (!Number.isFinite(count)) return 0;
      return Math.max(0, Math.min(999, Math.round(count)));
    }

    sanitize(eventName, data = {}) {
      const allowedFields = EVENT_FIELDS[eventName] || [];
      const safe = {
        schema_version: 1,
      };
      const values = {
        page_type: this.context.page_type,
        locale: this.context.locale,
        source_product_handle: this.context.product_handle,
        ...data,
      };

      [...COMMON_FIELDS, ...allowedFields].forEach((field) => {
        const value = values[field];
        if (field.endsWith("_count")) {
          safe[field] = this.cleanCount(value);
          return;
        }

        const cleaned = this.cleanString(value);
        if (cleaned) safe[field] = cleaned;
      });

      return safe;
    }

    publish(eventName, data = {}) {
      if (!Object.hasOwn(EVENT_FIELDS, eventName)) return false;
      if (!this.analyticsAllowed()) return false;

      const publish = this.win.Shopify?.analytics?.publish;
      if (typeof publish !== "function") return false;

      try {
        const result = publish.call(
          this.win.Shopify.analytics,
          eventName,
          this.sanitize(eventName, data),
        );
        if (result && typeof result.catch === "function") result.catch(() => {});
        return true;
      } catch {
        return false;
      }
    }

    cartItemCount() {
      const counter =
        this.root.querySelector?.("[data-sx-cart-title-count]") ||
        this.root.querySelector?.("[data-sx-cart-count]");
      const match = counter?.textContent?.match(/\d+/);
      return match ? this.cleanCount(match[0]) : 0;
    }

    productHandleFromLink(element) {
      const link = element?.closest?.('a[href*="/products/"]');
      if (!link) return "";

      try {
        const path = new URL(link.href, this.win.location?.origin || "https://syvox.invalid").pathname;
        const match = path.match(/\/products\/([^/?#]+)/);
        return match ? decodeURIComponent(match[1]) : "";
      } catch {
        return "";
      }
    }

    recommendationData(element) {
      const quickRow = element?.closest?.("[data-sx-cart-quick-item]");
      if (quickRow) {
        const targetHandle =
          quickRow.dataset.sxCrossSellTarget
          || quickRow.dataset.handle
          || this.productHandleFromLink(quickRow);
        return {
          placement: quickRow.closest("[data-sx-cart-drawer]")
            ? "cart_drawer"
            : "cart_page",
          product_handle: targetHandle,
          source_product_handle: quickRow.dataset.sxCrossSellSource || "",
          target_product_handle: targetHandle,
          variant_id:
            quickRow.querySelector?.("[data-variant-id]")?.dataset.variantId || "",
          recommendation_id: quickRow.dataset.sxRecommendationId || "",
        };
      }

      const addOn = element?.closest?.("[data-sx-addon]");
      if (addOn) {
        const targetHandle =
          addOn.dataset.sxCrossSellTarget
          || addOn.dataset.sxCrossSellHandle
          || "";
        return {
          placement: "product_page_addon",
          product_handle: targetHandle,
          source_product_handle:
            addOn.dataset.sxCrossSellSource
            || this.context.product_handle
            || "",
          target_product_handle: targetHandle,
          variant_id: addOn.dataset.sxCrossSellVariant || "",
          recommendation_id: addOn.dataset.sxRecommendationId || "",
        };
      }

      return null;
    }

    visibilityRatio(element) {
      if (
        !element ||
        element.hidden ||
        element.closest?.("[hidden], [aria-hidden='true'], [inert]") ||
        typeof element.getBoundingClientRect !== "function"
      ) {
        return 0;
      }

      const rect = element.getBoundingClientRect();
      const viewportWidth =
        this.win.innerWidth || this.root.documentElement?.clientWidth || 0;
      const viewportHeight =
        this.win.innerHeight || this.root.documentElement?.clientHeight || 0;
      const elementArea = Math.max(0, rect.width) * Math.max(0, rect.height);
      if (!viewportWidth || !viewportHeight || !elementArea) return 0;

      const visibleWidth = Math.max(
        0,
        Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0),
      );
      const visibleHeight = Math.max(
        0,
        Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
      );
      return (visibleWidth * visibleHeight) / elementArea;
    }

    isVisible(element) {
      return this.visibilityRatio(element) >= 0.35;
    }

    impressionKey(data) {
      return [
        data.placement,
        data.source_product_handle,
        data.target_product_handle || data.product_handle,
        data.variant_id,
        data.recommendation_id,
      ].join("|");
    }

    recordRecommendationImpression(element) {
      if (!this.isVisible(element)) return;
      const data = this.recommendationData(element);
      if (!data?.product_handle) return;

      const key = this.impressionKey(data);
      if (this.seenImpressions.has(key)) return;
      if (this.publish("syvox:cross_sell_impression", data)) {
        this.seenImpressions.add(key);
      }
    }

    observeRecommendations() {
      const recommendations = [
        ...this.root.querySelectorAll?.("[data-sx-cart-quick-item], [data-sx-addon]") || [],
      ];
      if (!recommendations.length) return;

      if ("IntersectionObserver" in this.win) {
        this.recommendationObserver = new this.win.IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
                this.recordRecommendationImpression(entry.target);
              }
            });
          },
          { threshold: [0.35] },
        );
        recommendations.forEach((element) => this.recommendationObserver.observe(element));
        return;
      }

      recommendations.forEach((element) => this.recordRecommendationImpression(element));
    }

    scanVisibleRecommendations() {
      const recommendations =
        this.root.querySelectorAll?.("[data-sx-cart-quick-item], [data-sx-addon]") || [];
      recommendations.forEach((element) => this.recordRecommendationImpression(element));
    }

    drawerIsOpen(drawer) {
      return Boolean(
        drawer &&
        !drawer.hidden &&
        drawer.getAttribute("aria-hidden") !== "true" &&
        drawer.classList.contains("is-open"),
      );
    }

    observeCartDrawer() {
      const drawer = this.root.querySelector?.("[data-sx-cart-drawer]");
      if (!drawer) return;
      this.lastDrawerState = this.drawerIsOpen(drawer) ? "open" : "closed";

      if (!("MutationObserver" in this.win)) return;
      this.drawerObserver = new this.win.MutationObserver(() => {
        const nextState = this.drawerIsOpen(drawer) ? "open" : "closed";
        if (nextState === this.lastDrawerState) return;
        this.lastDrawerState = nextState;

        if (nextState === "open") {
          this.publish("syvox:cart_drawer_opened", {
            open_source: this.pendingDrawerSource || "automatic",
            cart_item_count: this.cartItemCount(),
          });
          this.pendingDrawerSource = "";
          this.scanVisibleRecommendations();
          return;
        }

        this.publish("syvox:cart_drawer_closed", {
          cart_item_count: this.cartItemCount(),
        });
        this.win.requestAnimationFrame(() => this.scanVisibleRecommendations());
      });
      this.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ["aria-hidden", "class", "hidden"],
      });
    }

    setupClickTracking() {
      this.root.addEventListener("click", (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const cartOpener = target.closest("[data-sx-cart-open]");
        if (cartOpener) {
          this.pendingDrawerSource =
            cartOpener.closest("[data-sx-header]") ? "header" : "page";
        }

        const filter = target.closest("[data-sx-collection-filter]");
        if (filter) {
          this.win.requestAnimationFrame(() => {
            const visibleCount = [
              ...this.root.querySelectorAll("[data-sx-collection-card]:not([hidden])"),
            ].length;
            this.publish("syvox:collection_filter_changed", {
              collection_handle: this.context.collection_handle,
              selected_filter: filter.dataset.sxCollectionFilter || "all",
              visible_product_count: visibleCount,
            });
          });
        }

        const recommendation = this.recommendationData(target);
        if (recommendation) {
          const checkbox = target.closest("[data-sx-addon-check]");
          const action = checkbox
            ? ""
            : target.closest("[data-sx-cart-quickadd]")
              ? "add"
              : target.closest('a[href*="/products/"]')
                ? "view"
                : "";
          if (action) {
            this.publish("syvox:cross_sell_clicked", {
              ...recommendation,
              action,
            });
          }
        }

        const checkout = target.closest(
          '[data-sx-cart-checkout], form[action*="/cart"] [name="checkout"]',
        );
        if (
          checkout &&
          !checkout.hasAttribute("disabled") &&
          checkout.getAttribute("aria-disabled") !== "true"
        ) {
          this.publish("syvox:checkout_cta_clicked", {
            placement: checkout.closest("[data-sx-cart-drawer]")
              ? "cart_drawer"
              : "cart_page",
            cart_item_count: this.cartItemCount(),
          });
        }

        const searchResult = target.closest(".sx-search-result");
        if (searchResult) {
          const results = [
            ...this.root.querySelectorAll?.(".sx-search-result") || [],
          ];
          this.publish("syvox:search_result_clicked", {
            product_handle: this.productHandleFromLink(searchResult),
            result_position: Math.max(0, results.indexOf(searchResult) + 1),
          });
        }
      });
    }

    setupChangeTracking() {
      this.root.addEventListener("change", (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        if (target.matches("[data-sx-addon-check]")) {
          if (!target.checked) return;
          const recommendation = this.recommendationData(target);
          if (recommendation) {
            this.publish("syvox:cross_sell_clicked", {
              ...recommendation,
              action: "select",
            });
          }
          return;
        }

        if (!target.matches('select[name="sort_by"], [data-sx-collection-sort]')) return;

        this.publish("syvox:collection_sort_changed", {
          collection_handle: this.context.collection_handle,
          sort_by: target.value || target.dataset.sxCollectionSort || "",
        });
      });
    }

    setupInternalEvents() {
      this.root.addEventListener("syvox:cross-sell-added", (event) => {
        const detail = event.detail && typeof event.detail === "object" ? event.detail : {};
        this.publish("syvox:cross_sell_added", detail);
      });
      this.root.addEventListener("syvox:recommendations-updated", () => {
        this.win.requestAnimationFrame(() => this.scanVisibleRecommendations());
      });
      this.root.addEventListener("visitorConsentCollected", () => {
        /*
         * No pre-consent queue is retained or replayed. Only recommendations
         * visible after the new consent state is active can create an event.
         */
        this.win.requestAnimationFrame(() => this.scanVisibleRecommendations());
      });
    }

    init() {
      if (this._initialized) return this;
      this._initialized = true;
      this.loadPrivacyApi();
      this.setupClickTracking();
      this.setupChangeTracking();
      this.setupInternalEvents();
      this.observeCartDrawer();
      this.observeRecommendations();
      this.win.setTimeout(() => this.scanVisibleRecommendations(), 1000);
      return this;
    }
  }

  const api = Object.freeze({
    Tracker: SyvoxAnalyticsTracker,
    customEvents: Object.freeze(Object.keys(EVENT_FIELDS)),
    standardCoverage: STANDARD_EVENT_COVERAGE,
  });
  window.SyvoxAnalytics = api;

  const start = () => {
    window.syvoxAnalyticsTracker = new SyvoxAnalyticsTracker().init();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
